import React from 'react';
import {
	Box,
	Button,
	Container,
	ContentLayout,
	Header,
	Link,
	SpaceBetween,
	Table,
} from '@amzn/awsui-components-console';
import { generateTitleFromDBKey } from 'src/utils/CommonUtils';
import { SUB_WORKFLOW_TASK_TYPE } from 'src/constants/CommonConstants';
import { conditionalAttributesColumnDefinitions } from './taskDetailsReviewPanelConstants';
import EmptyState from 'src/components/commonComponents/table/emptyTableWithActions';
import { MarkdownCodeBlock } from 'src/components/commonComponents/MarkdownCodeBlock/MarkdownCodeBlock';
import { MarkdownContent } from 'src/components/commonComponents/MarkdownCodeBlock/MarkdownContent';
import { TaskDetailsPanelProps } from './taskDetailsReviewPanel.interface';

const MARKDOWN_DETAIL_FIELDS = ['taskDescription'];
const CODE_BLOCK_INPUT_FIELDS = ['shortdesc', 'title', 'description'];

const normalizeAttributeName = (name: string) =>
	name.trim().toLowerCase().replace(/[\s-]+/g, '_');

const isCodeBlockInputField = (attributeName: string) =>
	CODE_BLOCK_INPUT_FIELDS.includes(normalizeAttributeName(attributeName));

const GENERIC_API_TASK_TYPE = 'GenericAPITask';

const FIELD_LABEL_OVERRIDES: Record<string, string> = {
	genericAPITaskName: 'GENERIC API TASK NAME',
};

function TaskDetailsPanel({
	taskDetails,
	showTaskDetails,
}: TaskDetailsPanelProps) {
	const { taskInputs, taskType, childWorkflowId } = taskDetails;
	const childWorkflowHref = taskType === SUB_WORKFLOW_TASK_TYPE && childWorkflowId
		? `#/viewWorkflow?workflowId=${encodeURIComponent(childWorkflowId)}`
		: undefined;
	const detailsToShow = [
		'taskName',
		'taskType',
		'genericAPITaskName',
		'stage',
		'taskDescription',
		'conditionalAttributes',
		'comment',
	];

	const shouldShowDetail = (key: string) => {
		if (taskDetails[key] === undefined || taskDetails[key] === null) {
			return false;
		}

		if (key === 'genericAPITaskName') {
			return taskDetails.taskType === GENERIC_API_TASK_TYPE;
		}
		return true;
	};

	return (
		<ContentLayout data-testid="taskDetailsReviewpanel">
			<Container
				data-testid="taskDetailsReviewpanelCntnr"
				header={
					<SpaceBetween size="m">
						<Header
							data-testid="taskDetailsReviewpanelHeading"
							actions={
								<>
									<Button
										data-testid="taskDetailsPanelClose"
										iconName="close"
										variant="icon"
										onClick={showTaskDetails}
									/>
								</>
							}
						>
							Task Details
						</Header>
					</SpaceBetween>
				}
			>
				<SpaceBetween size="s">
					{detailsToShow
						.filter(shouldShowDetail)
						.map((key) => (
							<Box key={key}>
								<b>{FIELD_LABEL_OVERRIDES[key] || generateTitleFromDBKey(key)}</b>
								<Box>
									{key === 'conditionalAttributes' ? (
										<Table
											items={taskDetails[key]}
											variant="embedded"
											wrapLines
											columnDefinitions={conditionalAttributesColumnDefinitions}
											empty={
												<EmptyState
													title="Conditions"
													subtitle="No conditions are associated with this task"
												/>
											}
										/>
									) : key === 'taskName' && childWorkflowHref ? (
										<Link href={childWorkflowHref} external data-testid="childWorkflowLink">
											{taskDetails.taskName}
										</Link>
									) : MARKDOWN_DETAIL_FIELDS.includes(key) ? (
										<MarkdownContent content={taskDetails[key]} />
									) : (
										taskDetails[key]
									)}
								</Box>
							</Box>
						))}

					{(taskInputs ?? []).map(({ attributeName, attributeId, value }) => (
						<Box key={attributeId}>
							{value && attributeName && (
								<>
									<b>{attributeName.toUpperCase()}</b>
									<Box>
										{isCodeBlockInputField(attributeName) ? (
											<MarkdownCodeBlock content={value} />
										) : (
											value
										)}
									</Box>
								</>
							)}
						</Box>
					))}
				</SpaceBetween>
			</Container>
		</ContentLayout>
	);
}

export default TaskDetailsPanel;
